import { useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import NO_IMAGE from "assets/images/no_image.jpeg";
import CSelect from "common/CSelect";
import { useFilterForm } from "common/FilterDrawer/FilterForm";
import FilterClearButton from "common/FilterElement/FilterClearButton";
import FilterContainer from "common/FilterElement/FilterContainer";
import FilterInput from "common/FilterElement/FilterInput";
import LinkTag from "common/LinkTag";
import Table from "common/Table";
import TableHeader from "common/Table/TableHeader";
import ActiveInActiveStatus from "common/Table/renderCell/ActiveInActiveStatus";
import EditButton from "common/detailElement/EditButton";
import {
  GET_ALL_PRODUCT_UOM_LIST,
  PRODUCT_UOM_LIST,
} from "component/UnitOfMeasure/graphql/query";
import { GET_ALL_WORKOUT_CATEGORY } from "component/WorkoutCategories/query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { INITIAL_DATA_TABLE_LIMIT, getFilteredValue, getImageUrl } from "utils";
import { ACTIVE_INACTIVE_STATUS } from "utils/constants";
import useDebounce from "utils/useDebounce";

const columns = [
  {
    field: "id",
    headerName: "ID",
    flex: 0.3,
    valueGetter: function ({ row }) {
      return row?.objectId;
    },
  },
  {
    field: "uomName",
    headerName: "Name",
    flex: 1,
    valueGetter: function ({ row }) {
      return row?.uomName ?? "-";
    },
  },
  {
    field: "primaryUnit",
    headerName: "Primary Unit",
    flex: 1,
  },
  {
    field: "secondaryUnit",
    headerName: "Secondary Unit",
    flex: 1,
  },
  {
    field: "action",
    headerName: "Action",
    flex: 0.5,
    sortable: false,
    renderCell: ({ row }) => {
      return (
        <LinkTag to={`update/${row?.objectId}`}>
          <EditButton />
        </LinkTag>
      );
    },
  },
];

const INITIAL_STATE = {
  primaryUnit: { type: "text", value: "" },
  secondaryUnit: { type: "text", value: "" },
  uomName: { type: "text", value: "" },
};

const ProductUom = () => {
  const navigate = useNavigate();

  const { filter, setFilter } = useFilterForm(INITIAL_STATE);
  const [rows, setRows] = useState([]);

  const { loading, error, data, fetchMore } = useQuery(
    GET_ALL_PRODUCT_UOM_LIST,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onCompleted: (res) => {
        let tempRes = res?.productUomList?.edges?.map((item) => item.node);
        setRows(tempRes);
      },
      variables: {
        first: INITIAL_DATA_TABLE_LIMIT,
      },
    }
  );

  const debounceFunction = useDebounce((value) => fetchMoreData(value), 500);

  const onClearFilter = () => {
    setFilter(INITIAL_STATE);
    fetchMore({
      variables: {
        first: INITIAL_DATA_TABLE_LIMIT,
      },
    });
  };

  const onChangeDebounce = (e) => {
    let name = e.target.name;
    let value = e.target.value;
    if (name === "type" && value === "") {
      onClearFilter();
      return;
    }

    let key = name;
    setFilter((state) => ({
      ...filter,
      [key]: { ...state[key], value: value },
    }));

    debounceFunction(
      getFilteredValue({ ...filter, [key]: { ...filter[key], value: value } })
    );
  };

  const fetchMoreData = async (variables) => {
    await fetchMore({
      variables: variables,
    });
  };

  return (
    <Box>
      <TableHeader
        title="Product UOM"
        showAddButton
        onClick={() => navigate("create")}
      />

      <FilterContainer>
        <FilterInput
          label="Name"
          name="uomName"
          value={filter["uomName"].value}
          onChange={onChangeDebounce}
        />
        <FilterInput
          label="Primary Unit"
          name="primaryUnit"
          value={filter["primaryUnit"].value}
          onChange={onChangeDebounce}
        />
        <FilterInput
          label="Secondary Unit"
          name="secondaryUnit"
          value={filter["secondaryUnit"].value}
          onChange={onChangeDebounce}
        />

        <FilterClearButton onClick={onClearFilter} />
      </FilterContainer>

      <Table
        columns={columns}
        rows={rows}
        loading={loading}
        isHiddenColumnFeature={true}
        fetchMore={fetchMore}
        totalCount={data?.productUomList?.totalCount}
        rowHeight={80}
      />
    </Box>
  );
};

export default ProductUom;
